import React, { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import IconAction from "@/components/IconAction";
import { FolderTree, Loader2, Play, ScanSearch, CheckCircle2, AlertTriangle } from "lucide-react";

/**
 * Iter-229 — Drive tree sanitizer (CEO only).
 * Scans clients still on the legacy layout (<Client>/002-BDC/<Mission>/…)
 * and migrates them to the canonical layout
 * (<Client>/<Mission>/001-Devis … 006-Autres documents), moving every file
 * (Drive move — IDs preserved, zero loss) and updating stored paths.
 */
export default function DriveSanitizeBlock() {
  const [scan, setScan] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(null); // client_id in progress
  const [applyingAll, setApplyingAll] = useState(false);
  const [reports, setReports] = useState({}); // client_id -> report
  // Iter-229c — cancelled invoices Drive sanitizer (history).
  const [cancStatus, setCancStatus] = useState(null);
  const [cancLoading, setCancLoading] = useState(false);
  const [cancApplying, setCancApplying] = useState(false);
  const [cancReport, setCancReport] = useState(null);

  const loadCancStatus = async () => {
    setCancLoading(true);
    try {
      const r = await api.get("/admin/invoices/cancelled-drive-status");
      setCancStatus(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status failed");
    } finally { setCancLoading(false); }
  };

  const applyCancSanitize = async () => {
    if (!window.confirm("Assainir l'historique des factures annulées ? L'ancien PDF Drive sera supprimé et remplacé par la version « FACTURE ANNULÉE » dans le sous-dossier dédié.")) return;
    setCancApplying(true);
    try {
      const r = await api.post("/admin/invoices/cancelled-drive-sanitize");
      setCancReport(r.data);
      toast.success(`${r.data.uploaded} facture(s) annulée(s) ré-archivée(s) sur le Drive`);
      loadCancStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sanitize failed");
    } finally { setCancApplying(false); }
  };

  // Iter-229d — unlock forfait milestones still tied to a cancelled invoice.
  const [unlocking, setUnlocking] = useState(false);
  const unlockMilestones = async () => {
    setUnlocking(true);
    try {
      const r = await api.post("/admin/invoices/cancelled-milestones-unlock");
      toast.success(`${r.data.unlocked} palier(s) déverrouillé(s)`);
      loadCancStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unlock failed");
    } finally { setUnlocking(false); }
  };

  const runScan = async () => {
    setScanning(true);
    try {
      const r = await api.get("/admin/drive-sanitize/scan");
      setScan(r.data);
      if ((r.data?.clients_count || 0) === 0) toast.success("Arborescence Drive déjà conforme — rien à assainir.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    } finally { setScanning(false); }
  };

  const applyOne = async (clientId) => {
    setApplying(clientId);
    try {
      const r = await api.post("/admin/drive-sanitize/apply", { client_id: clientId });
      setReports((prev) => ({ ...prev, [clientId]: r.data }));
      const rep = r.data || {};
      toast.success(`${rep.client_name}: ${rep.missions_migrated} mission(s), ${rep.files_moved} fichier(s) déplacé(s)`);
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Migration failed for ${clientId}`);
      return null;
    } finally { setApplying(null); }
  };

  const applyAll = async () => {
    if (!scan?.clients?.length) return;
    if (!window.confirm(`Assainir le Drive de ${scan.clients.length} client(s) ? Les fichiers seront déplacés (aucune perte — les IDs Drive sont conservés).`)) return;
    setApplyingAll(true);
    for (const c of scan.clients) {
      // eslint-disable-next-line no-await-in-loop
      await applyOne(c.client_id);
    }
    setApplyingAll(false);
    runScan();
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-6 space-y-4 mb-6" data-testid="drive-sanitize-block">
      <div className="flex items-center gap-3 text-zinc-900">
        <FolderTree size={18} className="text-zinc-700" />
        <h2 className="font-heading font-bold text-lg">Drive tree sanitizer</h2>
      </div>
      <p className="text-xs text-zinc-500 max-w-2xl leading-relaxed">
        Target layout: <code className="px-1 py-0.5 bg-zinc-100 rounded font-mono">Facturation → Client → Mission → 001-Devis … 006-Autres documents</code>.
        The scan lists clients still on the legacy layout (<code className="font-mono">Client/002-BDC/Mission/…</code>).
        Applying the migration creates the correct folders, <strong>moves</strong> every document (Drive move — file IDs and links stay valid, zero loss),
        trashes the emptied legacy folders and updates the stored paths retroactively.
      </p>
      <div className="flex items-center gap-2">
        <button
          onClick={runScan}
          disabled={scanning || applyingAll}
          data-testid="drive-sanitize-scan-btn"
          className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50"
        >
          {scanning ? <Loader2 size={14} className="animate-spin" /> : <ScanSearch size={14} />} Scan
        </button>
        {scan?.clients?.length > 0 && (
          <button
            onClick={applyAll}
            disabled={applyingAll || !!applying}
            data-testid="drive-sanitize-apply-all-btn"
            className="inline-flex items-center gap-2 px-4 h-10 bg-emerald-700 text-white text-sm rounded-md hover:bg-emerald-800 disabled:opacity-50"
          >
            {applyingAll ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
            Assainir tout ({scan.clients.length} client(s))
          </button>
        )}
      </div>
      {scan && (
        <div className="space-y-2" data-testid="drive-sanitize-results">
          <div className="text-[11px] font-mono text-zinc-500">
            {scan.clients_count} client(s) à migrer · {scan.missions_to_migrate_total} mission(s) sur l'ancienne arborescence
          </div>
          {(scan.clients || []).map((c) => {
            const rep = reports[c.client_id];
            return (
              <div key={c.client_id} className="border border-zinc-200 rounded-md p-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`drive-sanitize-client-${c.client_id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium">{c.client_name}</div>
                  <div className="text-[11px] text-zinc-500 font-mono mt-0.5">
                    {c.missions_to_migrate.length} mission(s) à migrer
                    {c.legacy_client_folders.length > 0 && <> · dossiers legacy : {c.legacy_client_folders.join(", ")}</>}
                  </div>
                  {c.missions_to_migrate.length > 0 && (
                    <div className="text-[11px] text-zinc-600 mt-1">
                      {c.missions_to_migrate.map((m) => m.title).join(" · ")}
                    </div>
                  )}
                  {rep && (
                    <div className="mt-1.5 text-[11px] font-mono flex items-center gap-1.5">
                      {rep.errors?.length > 0
                        ? <span className="text-amber-700 inline-flex items-center gap-1"><AlertTriangle size={11} /> {rep.errors.length} erreur(s)</span>
                        : <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={11} /> OK</span>}
                      <span className="text-zinc-500">· {rep.missions_migrated} mission(s), {rep.files_moved} fichier(s) déplacé(s), {rep.folders_trashed} dossier(s) legacy supprimé(s)</span>
                    </div>
                  )}
                </div>
                <IconAction icon={Play} size="lg" testid={`drive-sanitize-apply-${c.client_id}`} label="Assainir le dossier Drive de ce client" loading={applying === c.client_id} disabled={!!applying || applyingAll} className="shrink-0" onClick={() => applyOne(c.client_id)} />
              </div>
            );
          })}
          {scan.clients_count === 0 && (
            <div className="text-sm text-emerald-700 inline-flex items-center gap-1.5" data-testid="drive-sanitize-clean">
              <CheckCircle2 size={14} /> Arborescence conforme — rien à migrer.
            </div>
          )}
        </div>
      )}

      {/* Iter-229c — Cancelled invoices Drive history sanitizer */}
      <div className="border-t border-zinc-200 pt-4 space-y-3" data-testid="cancelled-invoices-sanitize-block">
        <div className="flex items-center gap-2 text-zinc-900">
          <AlertTriangle size={15} className="text-red-700" />
          <h3 className="font-heading font-bold text-sm">Factures annulées — assainissement de l'historique</h3>
        </div>
        <p className="text-xs text-zinc-500 max-w-2xl leading-relaxed">
          Pour chaque facture annulée non assainie : l'ancien PDF (sans mention) est supprimé du Drive,
          un nouveau PDF avec la mention diagonale rouge <strong>« FACTURE ANNULÉE + date »</strong> est généré
          et archivé dans le sous-dossier <code className="font-mono px-1 bg-zinc-100 rounded">Factures annulées</code> (créé automatiquement).
          Les nouvelles annulations sont traitées automatiquement.
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={loadCancStatus}
            disabled={cancLoading || cancApplying}
            data-testid="cancelled-invoices-status-btn"
            className="inline-flex items-center gap-2 px-4 h-10 bg-zinc-950 text-white text-sm rounded-md hover:bg-zinc-800 disabled:opacity-50"
          >
            {cancLoading ? <Loader2 size={14} className="animate-spin" /> : <ScanSearch size={14} />} Vérifier
          </button>
          {cancStatus && cancStatus.to_sanitize > 0 && (
            <button
              onClick={applyCancSanitize}
              disabled={cancApplying}
              data-testid="cancelled-invoices-sanitize-btn"
              className="inline-flex items-center gap-2 px-4 h-10 bg-red-700 text-white text-sm rounded-md hover:bg-red-800 disabled:opacity-50"
            >
              {cancApplying ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
              Assainir ({cancStatus.to_sanitize} facture(s))
            </button>
          )}
        </div>
        {cancStatus && (
          <div className="text-[11px] font-mono text-zinc-600" data-testid="cancelled-invoices-status">
            {cancStatus.total_cancelled} facture(s) annulée(s) au total ·{" "}
            {cancStatus.to_sanitize === 0
              ? <span className="text-emerald-700">historique conforme ✔</span>
              : <span className="text-red-700">{cancStatus.to_sanitize} à assainir : {(cancStatus.pending || []).slice(0, 8).map((p) => p.number).join(", ")}{cancStatus.to_sanitize > 8 ? "…" : ""}</span>}
          </div>
        )}
        {/* Iter-229d — paliers forfait encore verrouillés par une facture annulée */}
        {cancStatus && (
          <div data-testid="cancelled-milestones-status">
            {(cancStatus.locked_milestones || []).length === 0 ? (
              <div className="text-[11px] font-mono text-emerald-700">Paliers : aucun palier verrouillé par une facture annulée ✔</div>
            ) : (
              <div className="border border-amber-200 bg-amber-50 rounded-md p-3 space-y-2">
                <div className="text-[11px] font-mono text-amber-800">
                  {(cancStatus.locked_milestones || []).length} palier(s) encore verrouillé(s) par une facture annulée :
                </div>
                <div className="text-[11px] text-amber-900">
                  {(cancStatus.locked_milestones || []).map((l, i) => (
                    <div key={i}>• {l.number} — {l.mission_title} / {l.milestone_name} ({l.milestone_status})</div>
                  ))}
                </div>
                <button
                  onClick={unlockMilestones}
                  disabled={unlocking}
                  data-testid="cancelled-milestones-unlock-btn"
                  className="inline-flex items-center gap-2 px-4 h-9 bg-amber-700 text-white text-xs rounded-md hover:bg-amber-800 disabled:opacity-50"
                >
                  {unlocking ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
                  Déverrouiller les paliers
                </button>
              </div>
            )}
          </div>
        )}
        {cancReport && (
          <div className="text-[11px] font-mono flex items-center gap-1.5" data-testid="cancelled-invoices-report">
            {cancReport.errors?.length > 0
              ? <span className="text-amber-700 inline-flex items-center gap-1"><AlertTriangle size={11} /> {cancReport.errors.length} erreur(s)</span>
              : <span className="text-emerald-700 inline-flex items-center gap-1"><CheckCircle2 size={11} /> OK</span>}
            <span className="text-zinc-500">· {cancReport.uploaded}/{cancReport.scanned} facture(s) ré-archivée(s) dans « Factures annulées »</span>
          </div>
        )}
      </div>
    </div>
  );
}
